/**
 * Pure eSSVI slice math for the IV Skew derived overlays.
 *
 * JavaScript port of the slice evaluation in models/essvi.py (lines 47-102):
 * the served smile rows carry the fitted slice params
 * {forward, tau, theta, rho, phi} verbatim, so the frontend can evaluate
 * the fitted curve, its ATM shape, and the implied settlement distribution
 * without any backend round trip. Everything here works in LOG-MONEYNESS
 * k = ln(K/F) with K and F in the row's native (SPX) space; callers
 * convert derived PRICES to ES with the same per-day ratio the dots use.
 *
 * Parity with the Python reference is pinned by
 * test/diag_essvi_js_parity.py; any edit here must keep that gate green.
 * Descriptive-only: the distribution is RISK-NEUTRAL, not real-world odds.
 */

/** Slice total variance w(k) (models/essvi.py:47-65, incl. the 1e-12 sqrt floor). */
export function sviTotalVariance(k, theta, rho, phi) {
  const u = phi * k + rho;
  const s = Math.sqrt(Math.max(u * u + (1 - rho * rho), 1e-12));
  return (theta / 2) * (1 + rho * phi * k + s);
}

/** dw/dk, analytic. */
export function sviTotalVarianceD1(k, theta, rho, phi) {
  const u = phi * k + rho;
  const s = Math.sqrt(Math.max(u * u + (1 - rho * rho), 1e-12));
  return (theta / 2) * (rho * phi + (phi * u) / s);
}

/**
 * IV at log-moneyness k. Mirrors svi_iv (models/essvi.py:77-102): NaN when
 * the slice is degenerate, otherwise sqrt(w/tau) clipped to [0.01, 5.0].
 */
export function sviIvFromK(k, { tau, theta, rho, phi }) {
  if (!(tau > 1e-10) || !(theta > 0)) return NaN;
  const w = sviTotalVariance(k, theta, rho, phi);
  const iv = Math.sqrt(Math.max(w, 0) / Math.max(tau, 1e-10));
  return Math.min(Math.max(iv, 0.01), 5.0);
}

/** IV at a strike in the slice's native (SPX) space; k = ln(max(K/F, 1e-10)). */
export function sviIv(strike, params) {
  const F = params.forward;
  if (!(F > 0) || !(strike > 0)) return NaN;
  return sviIvFromK(Math.log(Math.max(strike / F, 1e-10)), params);
}

/** ATM smile slope d(sigma)/dk at k=0: dw/dk|0 / (2*sqrt(w0*tau)); w0 = theta. */
export function atmSkew({ tau, theta, rho, phi }) {
  if (!(tau > 1e-10) || !(theta > 0)) return NaN;
  return (theta * rho * phi) / (2 * Math.sqrt(theta * tau));
}

/** ATM total-variance curvature d2w/dk2 at k=0: (theta/2) * phi^2 * (1 - rho^2). */
export function atmCurvature({ theta, rho, phi }) {
  if (!(theta > 0)) return NaN;
  return (theta / 2) * phi * phi * (1 - rho * rho);
}

const SQRT_2PI = Math.sqrt(2 * Math.PI);

export function normPdf(x) {
  return Math.exp(-0.5 * x * x) / SQRT_2PI;
}

/**
 * High-precision standard normal CDF (Hart 1968 rational approximation as
 * published by West 2005, ~1e-15 absolute). The textbook Abramowitz-Stegun
 * erf approximation is not accurate enough for the Python parity gate,
 * which cross-checks against math.erfc.
 */
export function normCdf(x) {
  const xabs = Math.abs(x);
  let c;
  if (xabs > 37) {
    c = 0;
  } else {
    const e = Math.exp(-xabs * xabs / 2);
    if (xabs < 7.07106781186547) {
      let num = 3.52624965998911e-2 * xabs + 0.700383064443688;
      num = num * xabs + 6.37396220353165;
      num = num * xabs + 33.912866078383;
      num = num * xabs + 112.079291497871;
      num = num * xabs + 221.213596169931;
      num = num * xabs + 220.206867912376;
      let den = 8.83883476483184e-2 * xabs + 1.75566716318264;
      den = den * xabs + 16.064177579207;
      den = den * xabs + 86.7807322029461;
      den = den * xabs + 296.564248779674;
      den = den * xabs + 637.333633378831;
      den = den * xabs + 793.826512519948;
      den = den * xabs + 440.413735824752;
      c = (e * num) / den;
    } else {
      let build = xabs + 0.65;
      build = xabs + 4 / build;
      build = xabs + 3 / build;
      build = xabs + 2 / build;
      build = xabs + 1 / build;
      c = e / (build * 2.506628274631);
    }
  }
  return x > 0 ? 1 - c : c;
}

/**
 * Risk-neutral P(S_T <= K) at log-moneyness k, from the fitted slice with
 * the smile-slope correction (Breeden-Litzenberger via dC/dK, undiscounted
 * Black-76 form; the q=r drift convention of the engine makes F the
 * expected settle):
 *   d2 = (-k - w/2)/sqrt(w)
 *   CDF = N(-d2) + n(d2) * (dw/dk) / (2*sqrt(w))
 */
export function riskNeutralCdf(k, { tau, theta, rho, phi }) {
  if (!(tau > 1e-10) || !(theta > 0)) return NaN;
  const w = Math.max(sviTotalVariance(k, theta, rho, phi), 1e-12);
  const sw = Math.sqrt(w);
  const d2 = (-k - w / 2) / sw;
  const slope = sviTotalVarianceD1(k, theta, rho, phi);
  return normCdf(-d2) + (normPdf(d2) * slope) / (2 * sw);
}

/**
 * Invert the risk-neutral CDF for a probability q via bisection on k.
 * Returns k (log-moneyness) or null when no bracket exists (degenerate
 * slice or non-monotone tail artifact: caller skips the layer).
 */
export function percentileK(q, params) {
  if (!(q > 0) || !(q < 1)) return null;
  let lo = -0.05, hi = 0.05;
  for (let i = 0; i < 60 && riskNeutralCdf(lo, params) > q; i++) lo *= 1.5;
  for (let i = 0; i < 60 && riskNeutralCdf(hi, params) < q; i++) hi *= 1.5;
  const fLo = riskNeutralCdf(lo, params);
  const fHi = riskNeutralCdf(hi, params);
  if (!(fLo <= q && q <= fHi)) return null;
  for (let i = 0; i < 100; i++) {
    const mid = (lo + hi) / 2;
    if (riskNeutralCdf(mid, params) < q) lo = mid; else hi = mid;
  }
  return (lo + hi) / 2;
}

/**
 * Log-moneyness of the risk-neutral density mode: numeric max of the
 * central-difference density over a grid spanning the 0.1%..99.9% mass,
 * with one parabolic refinement (display-grade precision).
 */
export function densityModeK(params) {
  const kLo = percentileK(0.001, params);
  const kHi = percentileK(0.999, params);
  if (kLo === null || kHi === null || !(kHi > kLo)) return null;
  const n = 401;
  const h = (kHi - kLo) / (n - 1);
  let bestI = -1, bestD = -Infinity;
  const cdf = new Array(n);
  for (let i = 0; i < n; i++) cdf[i] = riskNeutralCdf(kLo + i * h, params);
  for (let i = 1; i < n - 1; i++) {
    const d = (cdf[i + 1] - cdf[i - 1]) / (2 * h);
    if (Number.isFinite(d) && d > bestD) { bestD = d; bestI = i; }
  }
  if (bestI < 1) return null;
  // Parabolic refinement on the three central-difference samples
  const dPrev = (cdf[bestI] - cdf[bestI - 2 < 0 ? 0 : bestI - 2]) / (2 * h);
  const dNext = (cdf[bestI + 2 > n - 1 ? n - 1 : bestI + 2] - cdf[bestI]) / (2 * h);
  const denom = dPrev - 2 * bestD + dNext;
  const shift = Math.abs(denom) > 1e-18 ? (0.5 * (dPrev - dNext)) / denom : 0;
  const clamped = Math.max(-1, Math.min(1, shift));
  return kLo + (bestI + clamped) * h;
}
